import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { User } from '../../types';
import { globalSearch, SearchResult } from '../../utils/globalSearch';
import { useLanguageNavigate } from '../../hooks/useLanguageNavigate';

interface AppBarProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onSearchKeyDown?: (e: React.KeyboardEvent) => void;
  activeTab: string;
  user: User;
  onLanguageChange?: (language: string) => void;
  onUserClick?: () => void;
  onSettingsClick?: () => void;
}

export function AppBar({ searchTerm, onSearchChange, onSearchKeyDown, activeTab, user, onUserClick, onSettingsClick }: AppBarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const langNavigate = useLanguageNavigate();
  const isRTL = i18n.language === 'he';
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showResults, setShowResults] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get placeholder text - now global search for all screens
  const getPlaceholder = () => {
    return t('appBar.globalSearch') || 'Search patients, meetings, tasks, forms, case IDs...';
  };

  // Perform search when search term changes
  useEffect(() => {
    const performSearch = async () => {
      if (!searchTerm || searchTerm.trim().length < 2) {
        setSearchResults([]);
        setShowResults(false);
        return;
      }

      setIsSearching(true);
      setShowResults(true);
      try {
        const results = await globalSearch(searchTerm);
        setSearchResults(results);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Error performing global search:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!showResults) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === 'Enter' && selectedIndex >= 0 && searchResults[selectedIndex]) {
        e.preventDefault();
        handleSelectResult(searchResults[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowResults(false);
        setSearchResults([]);
        onSearchChange('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showResults, searchResults, selectedIndex]);

  const handleSelectResult = useCallback((result: SearchResult) => {
    switch (result.type) {
      case 'patient':
        if (result.caseId) {
          langNavigate(`/patient/${result.caseId}`);
          setShowResults(false);
          setSearchResults([]);
          onSearchChange('');
        }
        break;
      case 'event':
        if (result.caseId) {
          langNavigate(`/patient/${result.caseId}?event=${result.id}`);
          setShowResults(false);
          setSearchResults([]);
          onSearchChange('');
        }
        break;
      case 'task':
        if (result.caseId) {
          langNavigate(`/patient/${result.caseId}?task=${result.id}`);
          setShowResults(false);
          setSearchResults([]);
          onSearchChange('');
        }
        break;
      case 'milestone':
        if (result.caseId) {
          langNavigate(`/patient/${result.caseId}?milestone=${result.id}`);
          setShowResults(false);
          setSearchResults([]);
          onSearchChange('');
        }
        break;
      case 'form':
        setShowResults(false);
        setSearchResults([]);
        onSearchChange('');
        break;
    }
  }, [langNavigate, onSearchChange]);

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'patient': return t('search.patient') || 'Patient';
      case 'event': return t('search.event') || 'Meeting';
      case 'task': return t('search.task') || 'Task';
      case 'form': return t('search.form') || 'Intake Form';
      case 'milestone': return t('search.milestone') || 'Milestone';
      default: return type;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'patient': return '👤';
      case 'event': return '📅';
      case 'task': return '✅';
      case 'form': return '📋';
      case 'milestone': return '🎯';
      default: return '📄';
    }
  };

  const groupedResults = searchResults.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  return (
    <div 
      className="global-appbar"
      style={{
        background: 'white',
        borderBottom: '1px solid #e9ecef',
        padding: '16px 20px',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        width: '100%',
        height: '72px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        // Offset for sidebar width on desktop only (280px sidebar width)
        ...(!isMobile 
          ? (isRTL ? { marginRight: '280px' } : { marginLeft: '280px' })
          : {}
        )
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%'
      }}>
        {/* Search Input with User Info */}
        <div style={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          flexDirection: 'row',
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          {/* Search Input */}
          <div style={{ position: 'relative', flex: 1 }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={getPlaceholder()}
              value={searchTerm}
              onChange={(e) => {
                onSearchChange(e.target.value);
                if (e.target.value.trim().length >= 2) {
                  setShowResults(true);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchTerm.trim().length >= 2) {
                  e.preventDefault();
                  // Navigate to search results page
                  const currentLang = i18n.language || 'he';
                  langNavigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
                  setShowResults(false);
                } else if (onSearchKeyDown) {
                  onSearchKeyDown(e);
                }
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#007acc';
                if (searchResults.length > 0) {
                  setShowResults(true);
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ced4da';
                // Delay hiding results to allow click on result
                setTimeout(() => setShowResults(false), 200);
              }}
              style={{
                width: '100%',
                padding: isRTL ? '10px 16px 10px 40px' : '10px 40px 10px 16px',
                border: '1px solid #ced4da',
                borderRadius: showResults && searchResults.length > 0 ? '8px 8px 0 0' : '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s',
                direction: isRTL ? 'rtl' : 'ltr'
              }}
            />
            <span style={{
              position: 'absolute',
              [isRTL ? 'left' : 'right']: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              fontSize: '18px',
              pointerEvents: 'none'
            }}>
              🔍
            </span>

            {/* Search Results Dropdown */}
            {showResults && (searchResults.length > 0 || isSearching) && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ced4da',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                maxHeight: '400px',
                overflowY: 'auto',
                zIndex: 10001,
                marginTop: '-1px'
              }}>
                {isSearching ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    {t('common.loading') || 'Searching...'}
                  </div>
                ) : searchResults.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    {t('search.noResults') || 'No results found'}
                  </div>
                ) : (
                  <div style={{ padding: '8px' }}>
                    {Object.entries(groupedResults).map(([type, typeResults]) => (
                      <div key={type} style={{ marginBottom: '16px' }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#666',
                          marginBottom: '8px',
                          padding: '0 8px',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {getTypeIcon(type)} {getTypeLabel(type)} ({typeResults.length})
                        </div>
                        {typeResults.map((result, index) => {
                          const globalIndex = searchResults.indexOf(result);
                          const isSelected = globalIndex === selectedIndex;
                          
                          return (
                            <div
                              key={result.id}
                              onClick={() => handleSelectResult(result)}
                              onMouseEnter={() => setSelectedIndex(globalIndex)}
                              style={{
                                padding: '10px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                marginBottom: '4px',
                                backgroundColor: isSelected ? '#e7f3ff' : 'transparent',
                                border: isSelected ? '2px solid #007acc' : '2px solid transparent',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <div style={{ fontWeight: '500', marginBottom: '2px', color: '#000', fontSize: '14px' }}>
                                {result.title}
                              </div>
                              {result.subtitle && (
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                  {result.subtitle}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notification Icon */}
          <div style={{
            position: 'relative',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <span style={{ fontSize: '24px' }}>🔔</span>
            {/* Notification badge */}
            <span style={{
              position: 'absolute',
              top: '6px',
              right: '6px',
              background: '#dc3545',
              color: 'white',
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: '600',
              border: '2px solid white'
            }}>
              3
            </span>
          </div>

          {/* Settings Icon */}
          {onSettingsClick && (
            <div 
              onClick={onSettingsClick}
              style={{
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              title={t('navigation.settings') || 'Settings'}
            >
              <span style={{ fontSize: '24px' }}>⚙️</span>
            </div>
          )}

          {/* User Info - After Search */}
          <div 
            onClick={onUserClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexShrink: 0,
              flexDirection: 'row',
              background: '#f8f9fa',
              padding: '8px 12px',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e9ecef';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f8f9fa';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {user.picture && !user.picture.includes('placeholder') && user.picture.startsWith('http') ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #e9ecef'
                }}
              />
            ) : (
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #e9ecef'
              }}>
                <span style={{ fontSize: '20px' }}>👤</span>
              </div>
            )}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              textAlign: 'left'
            }}>
              <span style={{
                fontSize: '12px',
                color: '#666',
                fontWeight: '500'
              }}>
                {t('navigation.welcome')}
              </span>
              <span style={{
                fontSize: '14px',
                color: '#000',
                fontWeight: '600'
              }}>
                {user.name}
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

